import { BookOpen, FileText, FolderOpen, ShieldQuestion, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { authoredLines, type ControlGuidanceKey } from '@/lib/control-guidance';

/**
 * A CONTROL'S AUTHORED CONTENT, rendered the same way on both surfaces that show it — the Control Library's
 * detail window (as cards) and the workspace control page (inside Control Details) — so they read as one system.
 *
 * ⚠ THE TEXT IS THE AUTHOR'S, EXACTLY. Structure comes only from lines the author wrote (`authoredLines`):
 *   several lines become bullets (guidance) or evidence rows (expected evidence); a single line stays a
 *   paragraph. Nothing is split on punctuation, reordered or summarised.
 */

export type ControlSectionKey = 'description' | ControlGuidanceKey;

/** One icon per section, on every surface. */
export const SECTION_ICON: Record<ControlSectionKey, LucideIcon> = {
  description: FileText,
  implementation_guidance: BookOpen,
  applicability_guidance: ShieldQuestion,
  expected_evidence: FolderOpen,
};

/** The section's header: its icon and label. */
export function SectionHeading({ kind, label, className }: {
  kind: ControlSectionKey; label: string; className?: string;
}) {
  const Icon = SECTION_ICON[kind];
  return (
    <h3 className={cn(
      'flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-text-muted', className)}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {label}
    </h3>
  );
}

/**
 * The section's body. Expected evidence is a list of artefacts, so it always renders as evidence rows (one per
 * authored line); guidance with several authored lines renders as bullets; anything else is a paragraph.
 * `dense` matches the workspace control page's body size; the window reads at 14px.
 */
export function GuidanceBody({ kind, text, dense = false }: { kind: ControlSectionKey; text: string; dense?: boolean }) {
  const TEXT = cn(dense ? 'text-[13px] leading-[1.65]' : 'text-[14px] leading-[1.6]', 'text-text-primary');
  const items = authoredLines(text);
  if (kind === 'expected_evidence') {
    return (
      <ul className="flex flex-col gap-2" data-testid="evidence-rows">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 rounded-md bg-bg-surface-2 px-3 py-2">
            <FileText className="mt-[3px] h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
            <span className={cn(TEXT, 'min-w-0')}>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (kind !== 'description' && items.length > 1) {
    return (
      <ul className="flex flex-col gap-2" data-testid="guidance-bullets">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-text-muted" aria-hidden="true" />
            <span className={cn(TEXT, 'min-w-0')}>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className={cn(TEXT, 'whitespace-pre-wrap')}>{text.trim()}</p>;
}

/** The window's card: a bordered surface with the section header and body. */
export function GuidanceCard({ kind, label, text, testId }: {
  kind: ControlSectionKey; label: string; text: string; testId: string;
}) {
  return (
    <section data-testid={testId} className="rounded-lg border border-border-subtle bg-bg-surface p-4">
      <SectionHeading kind={kind} label={label} />
      <div className="mt-2 max-w-[68ch]">
        <GuidanceBody kind={kind} text={text} />
      </div>
    </section>
  );
}
